/** @prettier */

/**
 * 一括登録CSVフォーマットチェック＆変換関連関数一覧
 * データチェック関数は一括登録APIで使用するLambda関数「bulkRegistration」から引用
 */

// アップロードCSV例
// sl-system,outdoor-unit-name,outdoor-unit-address,outdoor-unit-input-voltage,outdoor-unit-power-supply,master-outdoor-unit,indoor-unit-name,indoor-unit-address,indoor-unit-type,interface-unit-name,interface-unit-address,interface-unit-type,interface-type,interface-unit-input-voltage,interface-unit-power-supply
// 1,OU000,0,100,threePhase,None,IU000,0,4way Cassette,,,,,
// 1,,,,,,,,,IF064,64,Lighting,SC-ADNA-E,100,threePhase

//Style import
import '../../css/common/common.css';
import * as Type from '../../Type/TypeDef';
import format from '../../define';
import i18n from 'i18next';

//prettier-ignore
/** csvファイルデータ格納位置 */
const csvColNum  = {
  slSystem       : 0,
  ouName         : 1,
  ouAddress      : 2,
  ouVoltage      : 3,
  powerSupply    : 4,
  parent         : 5,
  iuName         : 6,
  iuAddress      : 7,
  iuType         : 8,
  ifName         : 9,
  ifAddress      : 10,
  ifUnitType     : 11,
  ifType         : 12,
  ifVoltage      : 13,
  ifPowerSupply  : 14,
};

/** CSVラベル名の定数 */
const csvLabels = {
  slSystem: 'sl-system',
  ouName: 'outdoor-unit-name',
  ouAddress: 'outdoor-unit-address',
  ouVoltage: 'outdoor-unit-input-voltage',
  powerSupply: 'outdoor-unit-power-supply',
  parent: 'master-outdoor-unit',
  iuName: 'indoor-unit-name',
  iuAddress: 'indoor-unit-address',
  iuType: 'indoor-unit-type',
  ifName: 'interface-unit-name',
  ifAddress: 'interface-unit-address',
  ifUnitType: 'interface-unit-type',
  ifType: 'interface-type',
  ifVoltage: 'interface-unit-input-voltage',
  ifPowerSupply: 'interface-unit-power-supply',
};

/**
 * エラー内容に対応するi18nキーの定義。
 * React Hookは通常の関数内で使用できないため、i18nextのインスタンスを直接参照。
 */
const errorParameter = {
  emptyData: 'CollectiveSettingFunction.error.emptyData',
  label: 'CollectiveSettingFunction.error.label',
  numberOfItems: 'CollectiveSettingFunction.error.numberOfItems',
  dataConflict: 'CollectiveSettingFunction.error.dataConflict',
  dataLuck: 'CollectiveSettingFunction.error.dataLuck',
  unregistered: 'CollectiveSettingFunction.error.unregistered',
  slSystem: 'CollectiveSettingFunction.error.slSystem',
  ouName: 'CollectiveSettingFunction.error.outdoorUnitUame',
  ouAddress: 'CollectiveSettingFunction.error.outdoorUnitAddress',
  ouVoltage: 'CollectiveSettingFunction.error.outdoorUnitInputVoltage',
  powerSupply: 'CollectiveSettingFunction.error.outdoorUnitPowerSupply',
  parent: 'CollectiveSettingFunction.error.masterOutdoorUnit',
  iuAddress: 'CollectiveSettingFunction.error.indoorUnitAddress',
  iuType: 'CollectiveSettingFunction.error.indoorUnitType',
  ifAddress: 'CollectiveSettingFunction.error.interfaceUnitAddress',
  ifUnitType: 'CollectiveSettingFunction.error.interfaceUnitType',
  ifType: 'CollectiveSettingFunction.error.interfaceType',
  ifVoltage: 'CollectiveSettingFunction.error.interfaceUnitInputVoltage',
  ifPowerSupply: 'CollectiveSettingFunction.error.unitPowerSupply',
};

/** 室外機設定項目一覧 */
const ouSettingList = [
  csvLabels.ouName,
  csvLabels.ouAddress,
  csvLabels.ouVoltage,
  csvLabels.powerSupply,
  csvLabels.parent,
];
/** 室内機設定項目一覧 */
const iuSettingList = [csvLabels.iuName, csvLabels.iuAddress, csvLabels.iuType];
/** IF空調機設定項目一覧 */
const ifUnitSettingList = [
  csvLabels.ifName,
  csvLabels.ifAddress,
  csvLabels.ifUnitType,
  csvLabels.ifType,
  csvLabels.ifVoltage,
  csvLabels.ifPowerSupply,
];

/** 室外機最終設定項目番号 */
const OU_ITEM_LAST = ouSettingList.length;
/** 室内機最終設定項目番号 */
const IU_ITEM_LAST = ouSettingList.length + iuSettingList.length;
/** IF最終設定項目番号 */
const IF_ITEM_LAST =
  ouSettingList.length + iuSettingList.length + ifUnitSettingList.length;

/** データ存在チェック戻り値 */
const dataExistResponse = {
  allExist: 0, //全ての値が設定されている
  dataLack: 1, //一部の値が設定されている
  allNotExist: 2, //全ての値が設定されていない
};

/**
 * CSVデータを指定された設定に基づいて変換し、構造化されたデータを返します。
 *
 * @param readData - CSVデータ文字列
 * @param slType - スーパーリンク新旧('new','old')
 * @param isCurrentConfig 変換データが現在の構成情報か否か
 *
 * @returns 変換されたデータの配列、またはエラーコード
 */
const convertCSVData = (
  readData: string,
  slType: string,
  isCurrentConfig: boolean = false
) => {
  /** 空調機登録データ */
  let ouData: Type.CollectiveSettingData.collectiveAirconData[] = [];
  /** IF登録データ */
  let ifData: Type.CollectiveSettingData.collectiveInterfaceData[] = [];
  /** 登録済み室外アドレスリスト */
  let ouAddressList: {[prop: string]: string[]} = {1: [], 2: []};
  /** 登録済み室内アドレスリスト */
  let iuAddressList: {[prop: string]: string[]} = {1: [], 2: []};

  // １行ずつ分解して配列にする
  let splitData = readData.split(/\n|\r\n/);
  // データ最後に改行がある場合は配列末尾に空文字ができてしまうため取り除く
  if (splitData[splitData.length - 1] === '') {
    splitData.pop();
  }
  let labelData = splitData[0].split(',');

  //ラベルの項目数が不正の場合はエラーを返す
  if (labelData.length !== Object.keys(csvColNum).length) {
    return {
      errorCode: i18n.t(errorParameter.unregistered),
      parameter: [i18n.t(errorParameter.numberOfItems)],
    };
  }

  // ラベルが不正の場合はエラーを返す
  if (checkLabelData(labelData) === false) {
    return {
      errorCode: i18n.t(errorParameter.unregistered),
      parameter: [i18n.t(errorParameter.label)],
    };
  }
  // ラベル削除
  splitData.shift();

  //変換データが現在の構成情報じゃない場合
  if (isCurrentConfig === false) {
    //エクセル上で最終行を消すと、カンマのみの行が生成される場合がある。
    //エクセル上だと見落としがちなため、最終行からカンマのみの行を確認し、
    //存在する場合はその行をチェック対象から削除する。
    while (splitData.length > 0) {
      const lastLine = splitData[splitData.length - 1];
      const labelCount = Object.keys(csvColNum).length;
      const lastLineLabelCount = lastLine.split(',').length;

      //対象行の文字がカンマのみかつラベル数が一致している場合のみ削除する
      if (/^,+$/.test(lastLine) && lastLineLabelCount === labelCount) {
        splitData.pop();
      } else {
        //データが存在する行が来たらその時点で終了
        break;
      }
    }

    //登録データなし(ラベルのみ)の場合はエラーを返す
    if (splitData.length === 0) {
      return {
        errorCode: i18n.t(errorParameter.unregistered),
        parameter: [i18n.t(errorParameter.numberOfItems)],
      };
    }
  }

  // １行ずつデータをチェックし、
  // 機器登録データと登録済みアドレスリストにデータを追加する
  for (let rowData of splitData) {
    // カンマでデータ分割
    let columnData = rowData.split(',');
    //入力値の項目数をチェック
    if (columnData.length !== Object.keys(csvColNum).length) {
      return {
        errorCode: i18n.t(errorParameter.unregistered),
        parameter: [i18n.t(errorParameter.dataLuck)],
      };
    }

    //現在の構成情報の場合は登録データなしとして処理
    //新規登録の場合は異常データとして弾く
    if (isCurrentConfig === true && columnData.every(item => item === '')) {
      return {
        errorCode: '',
        parameter: ['initialize'],
      };
    }

    //SL系統のチェック
    if (slSystemValueCheck(columnData) === false) {
      return {
        errorCode: i18n.t(errorParameter.unregistered),
        parameter: [i18n.t(errorParameter.slSystem)],
      };
    }

    //空調機の種類（SL or SLIFで処理を分ける）
    if (checkOUDataExist(columnData) === dataExistResponse.allExist) {
      //空調機登録データの場合
      //同一行にIF設定項目が1つでも設定されている場合はエラー
      if (checkIFDataExist(columnData) !== dataExistResponse.allNotExist) {
        return {
          errorCode: i18n.t(errorParameter.unregistered),
          parameter: [i18n.t(errorParameter.dataConflict)],
        };
      }

      //室外機がすでに登録済み（処理済み）かどうかの判定
      const registeredOU = ouAddressList[
        columnData[csvColNum.slSystem]
      ].includes(columnData[csvColNum.ouAddress]);
      if (registeredOU === false) {
        //室外機が登録されていないの場合
        //室外機不正データチェック
        let ouCheckResult = ouDataCheck(columnData, slType);
        if (ouCheckResult.length > 0) {
          return {
            errorCode: i18n.t(errorParameter.unregistered),
            parameter: ouCheckResult,
          };
        }

        //室内機のデータがあるかの確認
        const existsIUData = checkIUDataExist(columnData);
        if (existsIUData === dataExistResponse.allExist) {
          //室内機を登録する場合
          //室内機不正データチェック
          let iuCheckResult = iuDataCheck(columnData, slType);
          if (iuCheckResult.length > 0) {
            return {
              errorCode: i18n.t(errorParameter.unregistered),
              parameter: iuCheckResult,
            };
          }
          //室内機アドレスの重複チェック
          const duplicatedAddr = iuAddressList[
            columnData[csvColNum.slSystem]
          ].includes(columnData[csvColNum.iuAddress]);
          if (duplicatedAddr) {
            return {
              errorCode: i18n.t(errorParameter.unregistered),
              parameter: [i18n.t(errorParameter.iuAddress)],
            };
          }

          //室外機・室内機登録
          ouData.push({
            name: columnData[csvColNum.ouName],
            slSystem: columnData[csvColNum.slSystem],
            address: columnData[csvColNum.ouAddress],
            parent: columnData[csvColNum.parent],
            powerSupply: columnData[csvColNum.powerSupply],
            voltage: columnData[csvColNum.ouVoltage],
            iu: [
              {
                name: columnData[csvColNum.iuName],
                address: columnData[csvColNum.iuAddress],
                iuType: columnData[csvColNum.iuType],
              },
            ],
          });

          //登録済みアドレスリスト更新
          ouAddressList[columnData[csvColNum.slSystem]].push(
            columnData[csvColNum.ouAddress]
          );
          iuAddressList[columnData[csvColNum.slSystem]].push(
            columnData[csvColNum.iuAddress]
          );
          continue;
        } else if (existsIUData === dataExistResponse.allNotExist) {
          //室外機のみ追加の場合
          ouData.push({
            name: columnData[csvColNum.ouName],
            slSystem: columnData[csvColNum.slSystem],
            address: columnData[csvColNum.ouAddress],
            parent: columnData[csvColNum.parent],
            powerSupply: columnData[csvColNum.powerSupply],
            voltage: columnData[csvColNum.ouVoltage],
            iu: [], //室内機は空配列で設定
          });

          //登録済みアドレスリスト更新
          ouAddressList[columnData[csvColNum.slSystem]].push(
            columnData[csvColNum.ouAddress]
          );
          continue;
        }
        return {
          errorCode: i18n.t(errorParameter.unregistered),
          parameter: [i18n.t(errorParameter.dataLuck)],
        };
      }

      //既に室外機登録済みの場合は室内機のみ追加する

      //室内機のデータの有無のチェック
      //チェックしないと室内機名が空欄で登録できてしまう
      const existsIUData = checkIUDataExist(columnData);

      if (existsIUData === dataExistResponse.dataLack) {
        return {
          errorCode: i18n.t(errorParameter.unregistered),
          parameter: [i18n.t(errorParameter.dataLuck)],
        };
      }
      //室外機不正データチェック
      let ouCheckResult = ouDataCheck(columnData, slType);
      if (ouCheckResult.length > 0) {
        return {
          errorCode: i18n.t(errorParameter.unregistered),
          parameter: ouCheckResult,
        };
      }
      //室内機不正データチェック
      let iuCheckResult = iuDataCheck(columnData, slType);
      if (iuCheckResult.length) {
        return {
          errorCode: i18n.t(errorParameter.unregistered),
          parameter: iuCheckResult,
        };
      }
      //室内機アドレスの重複チェック
      const duplicatedAddr = iuAddressList[
        columnData[csvColNum.slSystem]
      ].includes(columnData[csvColNum.iuAddress]);
      if (duplicatedAddr) {
        return {
          errorCode: i18n.t(errorParameter.unregistered),
          parameter: [i18n.t(errorParameter.iuAddress)],
        };
      }
      //室外機データと登録済みデータの整合性チェック
      let conflictCheck = ouDataMatchCheck(columnData, ouData);
      if (conflictCheck.length > 0) {
        return {
          errorCode: i18n.t(errorParameter.unregistered),
          parameter: conflictCheck,
        };
      }

      //対象室外機を検索して室内機を登録
      const addOUIndex = ouData.findIndex(ouInfo => {
        return (
          ouInfo.address === columnData[csvColNum.ouAddress] &&
          ouInfo.slSystem === columnData[csvColNum.slSystem]
        );
      });
      if (addOUIndex === -1) {
        continue;
      }
      ouData[addOUIndex].iu.push({
        name: columnData[csvColNum.iuName],
        address: columnData[csvColNum.iuAddress],
        iuType: columnData[csvColNum.iuType],
      });
      //登録済み室内アドレスリスト更新
      iuAddressList[columnData[csvColNum.slSystem]].push(
        columnData[csvColNum.iuAddress]
      );
      continue;
    } else if (checkIFDataExist(columnData) === dataExistResponse.allExist) {
      //IF登録データの場合
      //室内機・室外機データが設定されている場合はエラー
      if (
        checkOUDataExist(columnData) !== dataExistResponse.allNotExist ||
        checkIUDataExist(columnData) !== dataExistResponse.allNotExist
      ) {
        return {
          errorCode: i18n.t(errorParameter.unregistered),
          parameter: [i18n.t(errorParameter.dataConflict)],
        };
      }
      //IF不正データチェック
      let ifCheckData = ifUnitDataCheck(columnData, slType);
      if (ifCheckData.length) {
        return {
          errorCode: i18n.t(errorParameter.unregistered),
          parameter: ifCheckData,
        };
      }
      //アドレスの重複チェック
      const duplicatedAddr = iuAddressList[
        columnData[csvColNum.slSystem]
      ].includes(columnData[csvColNum.ifAddress]);
      if (duplicatedAddr) {
        return {
          errorCode: i18n.t(errorParameter.unregistered),
          parameter: [i18n.t(errorParameter.ifAddress)],
        };
      }

      //IF登録
      ifData.push({
        name: columnData[csvColNum.ifName],
        slSystem: columnData[csvColNum.slSystem],
        address: columnData[csvColNum.ifAddress],
        unitType: columnData[csvColNum.ifUnitType],
        ifType: columnData[csvColNum.ifType],
        powerSupply: columnData[csvColNum.ifPowerSupply],
        voltage: columnData[csvColNum.ifVoltage],
      });

      //登録済みアドレスリスト更新
      iuAddressList[columnData[csvColNum.slSystem]].push(
        columnData[csvColNum.ifAddress]
      );
      continue;
    }
    return {
      errorCode: i18n.t(errorParameter.unregistered),
      parameter: [i18n.t(errorParameter.dataLuck)],
    };
  }

  // ソート
  ouData = unitCompositionSort(ouData, 'sl');
  ifData = unitCompositionSort(ifData, 'slif');
  // １つの配列にデータを結合
  // SL1室外、SL1IF、SL2室外、SL2IFの順にする
  let ouData1: (
    | Type.CollectiveSettingData.collectiveAirconData
    | Type.CollectiveSettingData.collectiveInterfaceData
  )[] = ouData.filter(info => info.slSystem === '1');
  let ouData2: (
    | Type.CollectiveSettingData.collectiveAirconData
    | Type.CollectiveSettingData.collectiveInterfaceData
  )[] = ouData.filter(info => info.slSystem === '2');
  let ifData1 = ifData.filter(info => info.slSystem === '1');
  let ifData2 = ifData.filter(info => info.slSystem === '2');

  return ouData1.concat(ifData1).concat(ouData2.concat(ifData2));
};

/**
 * 構成情報ソート関数
 * @param {any} data ソートするデータ
 * @param {string} mode ソート対象
 * @returns {object[]} ソート済みデータ
 */
const unitCompositionSort = (data: any, mode: string) => {
  //室外機ソート
  data.sort(
    (
      a:
        | Type.CollectiveSettingData.collectiveAirconData
        | Type.CollectiveSettingData.collectiveInterfaceData,
      b:
        | Type.CollectiveSettingData.collectiveAirconData
        | Type.CollectiveSettingData.collectiveInterfaceData
    ) => {
      if (a.slSystem < b.slSystem) return -1;
      if (a.slSystem > b.slSystem) return 1;
      if (Number(a.address) < Number(b.address)) return -1;
      if (Number(a.address) > Number(b.address)) return 1;
      return 0;
    }
  );
  // IFの場合は室内機処理なしでリターン
  if (mode === 'slif') return data;

  //室内機ソート
  for (let i = 0; i < data.length; i++) {
    if ('iu' in data[i] === false) {
      continue;
    }
    if (data[i].iu.length === 0) {
      continue;
    }
    data[i].iu.sort(
      (
        a: Type.CollectiveSettingData.collectiveIndoorData,
        b: Type.CollectiveSettingData.collectiveIndoorData
      ) => {
        if (Number(a.address) < Number(b.address)) return -1;
        if (Number(a.address) > Number(b.address)) return 1;
        return 0;
      }
    );
  }

  return data;
};

/**
 * 画面表示用データ作成処理
 * @param {object[]} convData ソート済みデータ
 * @returns {string} 画面に表示する文字列
 */
const createDisplayData = (
  convData: (
    | Type.CollectiveSettingData.collectiveAirconData
    | Type.CollectiveSettingData.collectiveInterfaceData
  )[]
) => {
  /** 表示データ */
  let displayText = '';
  /** 現在参照中のデータのSL系統 */
  let nowSystem = convData[0].slSystem;
  displayText = `${displayText}SL${nowSystem} :\n`;

  //通常の関数内ではReact Hookは呼び出せないので、直接i18nextのインスタンスを使用
  const interfaceText = i18n.t(
    'CollectiveSettingFunction.description.interface'
  );
  const outdoorText = i18n.t(
    'CollectiveSettingFunction.description.outdoorUnit'
  );
  const indoorUnit = i18n.t('CollectiveSettingFunction.description.indoorUnit');

  // データを表示用に変換
  for (let info of convData) {
    // SL系統が変化した場合に画面に系統を表示
    if (info.slSystem !== nowSystem) {
      displayText = `${displayText}SL${info.slSystem} :\n`;
      nowSystem = info.slSystem;
    }
    // プロパティ「iu」の有無でIFのデータかを判定
    if ('iu' in info === false) {
      // IFデータ
      displayText = `${displayText}\t${interfaceText}: \n`;
      displayText = `${displayText}\t\t${csvLabels.ifName} : ${info.name}\n`;
      displayText = `${displayText}\t\t${csvLabels.ifAddress} : ${info.address}\n`;
    } else {
      // 室外機データ
      displayText = `${displayText}\t${outdoorText} : \n`;
      displayText = `${displayText}\t\t${csvLabels.ouName} : ${info.name}\n`;
      displayText = `${displayText}\t\t${csvLabels.ouAddress} : ${info.address}\n`;
    }

    // 室内機がいない場合は次のデータへ
    if ('iu' in info) {
      if (info.iu.length === 0) continue;
      // 室内機データの表示
      for (let iuInfo of info.iu) {
        displayText = `${displayText}\t\t\t${indoorUnit}: \n`;
        displayText = `${displayText}\t\t\t\t${csvLabels.iuName} : ${iuInfo.name}\n`;
        displayText = `${displayText}\t\t\t\t${csvLabels.iuAddress} : ${iuInfo.address}\n`;
      }
    }
  }
  return displayText;
};

/**
 * SL系統の値チェック
 * @param {string[]} arrayData チェック対象登録データ
 * @returns {boolean}チェックの可否(true:正常,false:不正)
 */
const slSystemValueCheck = (arrayData: string[]) => {
  let regex;
  //SLSystemのデータチェック
  regex = new RegExp(/^[1-2]$/);
  return regex.test(arrayData[csvColNum.slSystem]);
};

/**
 * 室外登録データの整合性チェック
 * @param {string[]} uncheckedOUData チェック対象登録データ
 * @param {string[]} checkedOUData チェック済み登録データ
 * @returns 不正な登録内容の項目リスト
 */
const ouDataMatchCheck = (
  uncheckedOUData: string[],
  checkedOUData: Type.CollectiveSettingData.collectiveAirconData[]
) => {
  let unMatchList: string[] = [];

  //整合性を確認するチェック済み登録データを探索
  const targetOUData = checkedOUData.find(
    (data: Type.CollectiveSettingData.collectiveAirconData) => {
      return (
        data.slSystem === uncheckedOUData[csvColNum.slSystem] &&
        data.address === uncheckedOUData[csvColNum.ouAddress]
      );
    }
  );
  if (targetOUData === undefined) {
    return unMatchList;
  }

  //OU名の一致チェック
  if (uncheckedOUData[csvColNum.ouName] !== targetOUData.name) {
    unMatchList.push(i18n.t(errorParameter.ouName));
  }
  //電源電圧一致チェック
  if (uncheckedOUData[csvColNum.ouVoltage] !== targetOUData.voltage) {
    unMatchList.push(i18n.t(errorParameter.ouVoltage));
  }
  //電源相数一致チェック
  if (uncheckedOUData[csvColNum.powerSupply] !== targetOUData.powerSupply) {
    unMatchList.push(i18n.t(errorParameter.powerSupply));
  }
  //親子設定の一致チェック
  if (uncheckedOUData[csvColNum.parent] !== targetOUData.parent) {
    unMatchList.push(i18n.t(errorParameter.parent));
  }

  return unMatchList;
};

/**
 * 室外機データの値チェック
 * @param {string[]} arrayData チェック対象登録データ
 * @param {string} slType スーパーリンク新旧('new','old')
 * @returns {string[]}不正データ項目のリスト
 */
const ouDataCheck = (arrayData: string[], slType: string) => {
  let dataNGList = [];
  let regex;

  //OUNameは多言語対応により自由記述。

  if (slType === 'new') {
    //OUAddress(新)のデータチェック
    regex = new RegExp(format.NEW_OU_ADDRESS_PATTERN_OK);
    if (regex.test(arrayData[csvColNum.ouAddress]) === false) {
      dataNGList.push(i18n.t(errorParameter.ouAddress));
    }
  } else {
    //OUAddress(旧)のデータチェック
    regex = new RegExp(format.OLD_OU_ADDRESS_PATTERN_OK);
    if (regex.test(arrayData[csvColNum.ouAddress]) === false) {
      dataNGList.push(i18n.t(errorParameter.ouAddress));
    }
  }

  //OUVoltageのデータチェック
  regex = new RegExp(format.VOLTAGE_PATTERN_OK);
  if (regex.test(arrayData[csvColNum.ouVoltage]) === false) {
    dataNGList.push(i18n.t(errorParameter.ouVoltage));
  }

  //PowerSupplyのデータチェック
  regex = new RegExp(format.POWERSUPPLY_PATTERN_OK);
  if (regex.test(arrayData[csvColNum.powerSupply]) === false) {
    dataNGList.push(i18n.t(errorParameter.powerSupply));
  }

  //Parentのデータチェック
  if (slType === 'new') {
    regex = new RegExp(format.NEW_OU_PARENT_PATTERN_OK);
    if (regex.test(arrayData[csvColNum.parent]) === false) {
      dataNGList.push(i18n.t(errorParameter.parent));
    }
  } else {
    regex = new RegExp(format.OLD_OU_PARENT_PATTERN_OK);
    if (regex.test(arrayData[csvColNum.parent]) === false) {
      dataNGList.push(i18n.t(errorParameter.parent));
    }
  }

  return dataNGList;
};

/**
 * 室内機データの値チェック
 * @param {string[]} arrayData チェック対象登録データ
 * @param {string} slType スーパーリンク新旧('new','old')
 * @returns {string[]}不正な登録データのリスト
 */
const iuDataCheck = (arrayData: string[], slType: string) => {
  let dataNGList = [];
  let regex;

  //IUNameは多言語対応により自由記述。

  if (slType === 'new') {
    //IUAddress(新)のデータチェック
    regex = new RegExp(format.NEW_IU_ADDRESS_PATTERN_OK);
    if (regex.test(arrayData[csvColNum.iuAddress]) === false) {
      dataNGList.push(i18n.t(errorParameter.iuAddress));
    }
  } else {
    //IUAddress(旧)のデータチェック
    regex = new RegExp(format.OLD_IU_ADDRESS_PATTERN_OK);
    if (regex.test(arrayData[csvColNum.iuAddress]) === false) {
      dataNGList.push(i18n.t(errorParameter.iuAddress));
    }
  }

  //IUTypeのデータチェック
  regex = new RegExp(format.IU_TYPE_PATTERN_OK);
  if (regex.test(arrayData[csvColNum.iuType]) === false) {
    dataNGList.push(i18n.t(errorParameter.iuType));
  }
  return dataNGList;
};

/**
 * IFデータの値チェック
 * @param {string[]} arrayData チェック対象登録データ
 * @param {string} slType スーパーリンク新旧('new','old')
 * @returns {string[]}不正な登録データのリスト
 */
const ifUnitDataCheck = (arrayData: string[], slType: string) => {
  let dataNGList = [];
  let regex;

  //IFNameは多言語対応により自由記述。

  if (slType === 'new') {
    //IFAddress(新)のデータチェック
    regex = new RegExp(format.NEW_IF_ADDRESS_PATTERN_OK);
    if (regex.test(arrayData[csvColNum.ifAddress]) === false) {
      dataNGList.push(i18n.t(errorParameter.ifAddress));
    }
  } else {
    //IFAddress(旧)のデータチェック
    regex = new RegExp(format.OLD_IF_ADDRESS_PATTERN_OK);
    if (regex.test(arrayData[csvColNum.ifAddress]) === false) {
      dataNGList.push(i18n.t(errorParameter.ifAddress));
    }
  }

  //IFUnitTypeのデータチェック
  regex = new RegExp(format.IF_UNIT_TYPE_PATTERN_OK);
  if (regex.test(arrayData[csvColNum.ifUnitType]) === false) {
    dataNGList.push(i18n.t(errorParameter.ifUnitType));
  }

  //IFTypeのデータチェック
  regex = new RegExp(format.IF_TYPE_PATTERN_OK);
  if (regex.test(arrayData[csvColNum.ifType]) === false) {
    dataNGList.push(i18n.t(errorParameter.ifType));
  }

  //IFVoltageのデータチェック
  regex = new RegExp(format.VOLTAGE_PATTERN_OK);
  if (regex.test(arrayData[csvColNum.ifVoltage]) === false) {
    dataNGList.push(i18n.t(errorParameter.ifVoltage));
  }

  //PowerSupplyのデータチェック
  regex = new RegExp(format.POWERSUPPLY_PATTERN_OK);
  if (regex.test(arrayData[csvColNum.ifPowerSupply]) === false) {
    dataNGList.push(i18n.t(errorParameter.ifPowerSupply));
  }

  return dataNGList;
};

/**
 * 室外機データの存在チェック
 * @param {string[]} arrayData チェック対象登録データ
 * @returns {number} 室外機データのデータ有無(0:全データあり, 1:一部データ欠損, 2:全データなし)
 */
const checkOUDataExist = (arrayData: string[]) => {
  let counter = 0;
  for (let i = 1; i <= OU_ITEM_LAST; i++) {
    if (arrayData[i] === '') {
      counter++;
    }
  }
  if (counter === 0) {
    return dataExistResponse.allExist;
  } else if (0 < counter && counter < OU_ITEM_LAST) {
    return dataExistResponse.dataLack;
  }
  return dataExistResponse.allNotExist;
};

/**
 * 室内機データの存在チェック
 * @param {string[]} arrayData チェック対象登録データ
 * @returns {number} 室内機データのデータ有無(0:全データあり, 1:一部データ欠損, 2:全データなし)
 */
const checkIUDataExist = (arrayData: string[]) => {
  let counter = 0;
  for (let i = OU_ITEM_LAST + 1; i <= IU_ITEM_LAST; i++) {
    if (arrayData[i] === '') {
      counter++;
    }
  }
  if (counter === 0) {
    return dataExistResponse.allExist;
  } else if (0 < counter && counter < IU_ITEM_LAST - OU_ITEM_LAST) {
    return dataExistResponse.dataLack;
  }
  return dataExistResponse.allNotExist;
};

/**
 * IFデータの存在チェック
 * @param {string[]} arrayData チェック対象登録データ
 * @returns {number} IFデータのデータ有無(0:全データあり, 1:一部データ欠損, 2:全データなし)
 */
const checkIFDataExist = (arrayData: string[]) => {
  let counter = 0;
  for (let i = IU_ITEM_LAST + 1; i <= IF_ITEM_LAST; i++) {
    if (arrayData[i] === '') {
      counter++;
    }
  }
  if (counter === 0) {
    return dataExistResponse.allExist;
  } else if (0 < counter && counter < IF_ITEM_LAST - IU_ITEM_LAST) {
    return dataExistResponse.dataLack;
  }
  return dataExistResponse.allNotExist;
};

/**
 * ラベルチェック
 * @param data csvデータラベル
 * @returns ラベルが正常かどうか
 */
const checkLabelData = (data: string[]) => {
  if (data.length !== Object.keys(csvColNum).length) return false;

  // ラベル値のチェックをループで処理
  const labelKeys = Object.keys(csvColNum) as (keyof typeof csvColNum)[];
  for (const key of labelKeys) {
    if (data[csvColNum[key]] !== csvLabels[key]) {
      return false;
    }
  }

  return true;
};

// 室内機タイプ変換テーブル
const iuTypeTable = [
  ['AC', 'AC General'],
  ['FDT', '4way Cassette'],
  ['FDTW', '2way Cassette'],
  ['FDTS', '1way Cassette'],
  ['FDE', 'Ceiling Suspended'],
  ['FDF', 'Floor Standing'],
  ['FDFW', 'Floor Standing Console'],
  ['FDK', 'Wall Mounted'],
  ['FDU', 'Duct Connected'],
];

// 空調機タイプ変換テーブル
const ifUnitTypeTable = [
  ['AC', 'AC General'],
  ['FDT', '4way Cassette'],
  ['FDTW', '2way Cassette'],
  ['FDTS', '1way Cassette'],
  ['FDE', 'Ceiling Suspended'],
  ['FDF', 'Floor Standing'],
  ['FDFW', 'Floor Standing Console'],
  ['FDK', 'Wall Mounted'],
  ['FDU', 'Duct Connected'],
  ['LIGHT', 'Lighting'],
  ['VENT', 'Ventilation'],
];

// インターフェースタイプ変換テーブル
const ifTypeTable = [['SC-ADNA', 'SC-ADNA-E']];
/**
 * CSVデータの室内機、空調機タイプを型式から名称に変換する。
 * 例: FDT => 4way Cassette
 * @param stringData
 * @returns 変換後のCSVデータ
 */
const convToIconName = (stringData: string) => {
  let splitData = stringData.split(/\n|\r\n/);
  // １行ずつ分解して配列にする
  // データ最後に改行がある場合は配列末尾に空文字ができてしまうため取り除く
  if (splitData[splitData.length - 1] === '') {
    splitData.pop();
  }
  // データがない場合
  if (splitData.length === 1) {
    return stringData;
  }

  /**型式から名称に変換 */
  const convert = (match: string, table: string[][]) => {
    const result = table.find(data => data[0] === match);
    if (result === undefined) return '';
    return result[1];
  };
  // replace用正規表現
  const IU_TYPE = /^AC$|^FDT$|^FDTW$|^FDTS$|^FDE$|^FDF$|^FDFW$|^FDK$|^FDU$/;
  const IF_UNIT_TYPE =
    /^AC$|^FDT$|^FDTW$|^FDTS$|^FDE$|^FDF$|^FDFW$|^FDK$|^FDU$|^LIGHT$|^VENT$/;

  // 変換後のCSVデータ
  let resultData = '';

  // １行ずつデータを変換する
  for (let rowData of splitData) {
    // カンマでデータ分割
    let columnData = rowData.split(',');

    //室内機タイプ変換
    columnData[csvColNum.iuType] = columnData[csvColNum.iuType].replace(
      IU_TYPE,
      e => convert(e, iuTypeTable)
    );

    //空調機タイプ変換
    columnData[csvColNum.ifUnitType] = columnData[csvColNum.ifUnitType].replace(
      IF_UNIT_TYPE,
      e => convert(e, ifUnitTypeTable)
    );

    //インターフェースタイプ変換
    columnData[csvColNum.ifType] = columnData[csvColNum.ifType].replace(
      'SC-ADNA',
      e => convert(e, ifTypeTable)
    );

    // データの連結
    if (resultData.length === 0) {
      // 1行目は改行を付けずにそのまま追加
      resultData += columnData.join();
    } else {
      // 2行目以降は改行を付けて追加
      resultData += '\n' + columnData.join();
    }
  }

  return resultData;
};

/**
 * CSVデータの室内機、空調機タイプを名称から型式に変換する。
 * 例: 4way Cassette => FDT
 * @param stringData
 * @returns 変換後のCSVデータ
 */
const convToModelName = (stringData: string) => {
  let splitData = stringData.split(/\n|\r\n/);

  // １行ずつ分解して配列にする
  // データ最後に改行がある場合は配列末尾に空文字ができてしまうため取り除く
  if (splitData[splitData.length - 1] === '') {
    splitData.pop();
  }
  // データがない場合
  if (splitData.length === 1) {
    return stringData;
  }

  /**名称から型式に変換 */
  const convert = (match: string, table: string[][]) => {
    const result = table.find(data => data[1] === match);
    if (result === undefined) return '';
    return result[0];
  };

  // 変換後のCSVデータ
  let resultData = '';

  // データの整合性チェックはconvertCSVDataで行うので省略
  // １行ずつデータを変換する
  for (let rowData of splitData) {
    const labelCount = Object.keys(csvColNum).length;
    const lastLineLabelCount = rowData.split(',').length;

    //対象行の文字がカンマのみかつラベル数が一致している場合、APIに渡すデータから削除する
    if (/^,+$/.test(rowData) && lastLineLabelCount === labelCount) {
      continue;
    }

    // カンマでデータ分割
    let columnData = rowData.split(',');

    //室内機タイプ変換
    columnData[csvColNum.iuType] = columnData[csvColNum.iuType].replace(
      format.IU_TYPE_PATTERN_OK,
      e => convert(e, iuTypeTable)
    );

    //空調機タイプ変換
    columnData[csvColNum.ifUnitType] = columnData[csvColNum.ifUnitType].replace(
      format.IF_UNIT_TYPE_PATTERN_OK,
      e => convert(e, ifUnitTypeTable)
    );

    columnData[csvColNum.ifType] = columnData[csvColNum.ifType].replace(
      'SC-ADNA-E',
      e => convert(e, ifTypeTable)
    );

    // データの連結
    if (resultData.length === 0) {
      // 1行目は改行を付けずにそのまま追加
      resultData += columnData.join();
    } else {
      // 2行目以降は改行を付けて追加
      resultData += '\n' + columnData.join();
    }
  }

  return resultData;
};

export {convertCSVData, createDisplayData, convToIconName, convToModelName};
